import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getAllTickets } from '../../../api/tickets';
import { getAllPhases } from '../../../api/phases';
import './BetaMilestoneView.scss';

const DAYS_TO_SHOW = 60;

const BetaMilestoneView = ({ onNavigate }) => {
  const { projects, usersMapByEmail, companyUsers } = useBetaData();
  const { viewingCompanyId } = useAuth();
  const [projectStats, setProjectStats] = useState([]);
  const [phasesByProject, setPhasesByProject] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewOffset, setViewOffset] = useState(0);
  const [selectedId, setSelectedId] = useState(null); // 'project-{id}' or 'phase-{id}'
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [expandedTickets, setExpandedTickets] = useState(false);
  const timelineRef = useRef(null);
  const headerScrollRef = useRef(null);
  const labelsRef = useRef(null);
  const scrollSyncSource = useRef(null);

  useEffect(() => {
    loadStats();
  }, [projects, viewingCompanyId]);

  const formatDateStr = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${y}/${parseInt(m)}/${parseInt(d)}`;
  };

  const loadStats = async () => {
    if (!projects || projects.length === 0) {
      setProjectStats([]);
      setPhasesByProject({});
      setLoading(false);
      return;
    }
    setLoading(true);

    const [{ data: allTickets }, { data: allPhasesData }] = await Promise.all([
      getAllTickets({ companyId: viewingCompanyId }),
      getAllPhases({ companyId: viewingCompanyId }),
    ]);

    // Tickets by project
    const ticketsByProject = {};
    (allTickets || []).forEach((t) => {
      if (!ticketsByProject[t.project_id]) ticketsByProject[t.project_id] = [];
      ticketsByProject[t.project_id].push(t);
    });

    // Phases by project
    const phasesMap = {};
    (allPhasesData || []).forEach((phase) => {
      if (!phasesMap[phase.project_id]) phasesMap[phase.project_id] = [];
      phasesMap[phase.project_id].push(phase);
    });
    setPhasesByProject(phasesMap);

    // Expand all projects that have phases by default
    const toExpand = new Set();
    Object.keys(phasesMap).forEach((pid) => {
      if (phasesMap[pid].length > 0) toExpand.add(Number(pid));
    });
    setExpandedProjects(toExpand);

    const results = projects.map((project) => {
      const ticketList = ticketsByProject[project.id] || [];
      const total = ticketList.length;
      const completed = ticketList.filter((t) => t.status === 'completed').length;
      const inProgress = ticketList.filter((t) => t.status === 'in_progress').length;
      const pending = ticketList.filter((t) => t.status === 'open').length;
      const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

      return {
        project,
        total,
        completed,
        inProgress,
        pending,
        percent,
        tickets: ticketList,
      };
    });

    setProjectStats(results);
    setLoading(false);
  };

  const projectsWithDates = useMemo(() => {
    return projectStats
      .filter((s) => s.project.milestone_start_date || s.project.milestone_end_date)
      .sort((a, b) => {
        const aDate = a.project.milestone_start_date || a.project.milestone_end_date;
        const bDate = b.project.milestone_start_date || b.project.milestone_end_date;
        return aDate.localeCompare(bDate);
      });
  }, [projectStats]);

  const projectsWithoutDates = useMemo(() => {
    return projectStats.filter(
      (s) => !s.project.milestone_start_date && !s.project.milestone_end_date
    );
  }, [projectStats]);

  // 時間軸計算
  const { startDate, dates } = useMemo(() => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 14 + viewOffset * 30);
    start.setHours(0, 0, 0, 0);

    const dateArray = [];
    for (let i = 0; i < DAYS_TO_SHOW; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      dateArray.push(d);
    }
    return { startDate: start, dates: dateArray };
  }, [viewOffset]);

  const dayWidth = 36;
  const todayIndex = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((today - startDate) / (1000 * 60 * 60 * 24));
  }, [startDate]);

  useEffect(() => {
    if (!timelineRef.current) return;
    const scrollLeft = todayIndex >= 0 && todayIndex < DAYS_TO_SHOW
      ? Math.max(0, (todayIndex - 3) * dayWidth)
      : 0;
    timelineRef.current.scrollLeft = scrollLeft;
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = scrollLeft;
  }, [loading, todayIndex]);

  const handleGridScroll = () => {
    if (scrollSyncSource.current === 'labels') {
      scrollSyncSource.current = null;
      return;
    }
    scrollSyncSource.current = 'grid';
    if (headerScrollRef.current && timelineRef.current) {
      headerScrollRef.current.scrollLeft = timelineRef.current.scrollLeft;
    }
    if (labelsRef.current && timelineRef.current) {
      labelsRef.current.scrollTop = timelineRef.current.scrollTop;
    }
  };

  const handleLabelsScroll = () => {
    if (scrollSyncSource.current === 'grid') {
      scrollSyncSource.current = null;
      return;
    }
    scrollSyncSource.current = 'labels';
    if (timelineRef.current && labelsRef.current) {
      timelineRef.current.scrollTop = labelsRef.current.scrollTop;
    }
  };

  const getBarPosition = (sd, ed) => {
    if (!sd || !ed) return null;
    const s = new Date(sd);
    const e = new Date(ed);
    s.setHours(0, 0, 0, 0);
    e.setHours(0, 0, 0, 0);

    const startOff = Math.round((s - startDate) / (1000 * 60 * 60 * 24));
    const endOff = Math.round((e - startDate) / (1000 * 60 * 60 * 24));
    const left = startOff * dayWidth;
    const width = Math.max((endOff - startOff + 1) * dayWidth - 4, dayWidth - 4);

    return { left, width };
  };

  const getProjectBarPosition = (project) => {
    const sd = project.milestone_start_date || project.milestone_end_date;
    const ed = project.milestone_end_date || project.milestone_start_date;
    return getBarPosition(sd, ed);
  };

  const getPhaseBarPosition = (phase) => {
    return getBarPosition(phase.start_date, phase.end_date);
  };

  const getPhaseProgress = (phase) => {
    let total = 0, done = 0;
    (phase.deliverables || []).forEach((d) => {
      const items = d.deliverable_items || [];
      total += items.length;
      done += items.filter((i) => i.is_completed).length;
    });
    return total === 0 ? 0 : Math.round((done / total) * 100);
  };

  const formatDate = (d) => {
    const month = d.getMonth() + 1;
    const day = d.getDate();
    return `${month}/${day}`;
  };

  const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
  const isToday = (d) => {
    const today = new Date();
    return d.toDateString() === today.toDateString();
  };

  const rangeLabel = useMemo(() => {
    if (dates.length === 0) return '';
    const first = dates[0];
    const last = dates[dates.length - 1];
    const fy = first.getFullYear();
    const fm = first.getMonth() + 1;
    const ly = last.getFullYear();
    const lm = last.getMonth() + 1;
    if (fy === ly) {
      return fm === lm ? `${fy}年 ${fm}月` : `${fy}年 ${fm}月 - ${lm}月`;
    }
    return `${fy}年 ${fm}月 - ${ly}年 ${lm}月`;
  }, [dates]);

  const toggleProjectExpand = (projectId, e) => {
    e.stopPropagation();
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const handleProjectClick = (projectId) => {
    const key = `project-${projectId}`;
    setSelectedId((prev) => (prev === key ? null : key));
    setExpandedTickets(false);
  };

  const handlePhaseClick = (phaseId, e) => {
    e.stopPropagation();
    const key = `phase-${phaseId}`;
    setSelectedId((prev) => (prev === key ? null : key));
    setExpandedTickets(false);
  };

  // Selected detail data
  const selectedDetail = useMemo(() => {
    if (!selectedId) return null;
    const [type, id] = selectedId.split('-');
    if (type === 'project') {
      return {
        type: 'project',
        data: projectStats.find((s) => s.project.id === Number(id)) || null,
      };
    }
    if (type === 'phase') {
      for (const phases of Object.values(phasesByProject)) {
        const phase = phases.find((p) => p.id === Number(id));
        if (phase) {
          const stat = projectStats.find((s) => s.project.id === phase.project_id);
          return { type: 'phase', data: phase, projectStat: stat };
        }
      }
    }
    return null;
  }, [selectedId, projectStats, phasesByProject]);

  const getUserInfo = (userId) => {
    return (companyUsers || []).find((u) => u.id === userId);
  };

  // Render grid cells helper
  const renderGridCells = () =>
    dates.map((d, i) => (
      <div
        key={i}
        className={`grid-cell ${isWeekend(d) ? 'weekend' : ''} ${isToday(d) ? 'today' : ''}`}
        style={{ width: dayWidth }}
      />
    ));

  // Build flat row list for the gantt
  const buildRows = (statList, hasExpandArrow) => {
    const rows = [];
    statList.forEach((stat) => {
      const p = stat.project;
      const phases = phasesByProject[p.id] || [];
      const isExpanded = expandedProjects.has(p.id);
      rows.push({ type: 'project', stat, hasPhases: phases.length > 0, isExpanded, hasExpandArrow });
      if (isExpanded && phases.length > 0) {
        phases.forEach((phase) => {
          rows.push({ type: 'phase', phase, projectColor: p.color || '#667eea', projectId: p.id });
        });
      }
    });
    return rows;
  };

  if (loading) {
    return (
      <div className="beta-milestone-view">
        <div className="milestone-loading">載入中...</div>
      </div>
    );
  }

  if (projectStats.length === 0) {
    return (
      <div className="beta-milestone-view">
        <div className="milestone-empty">尚無專案</div>
      </div>
    );
  }

  const rowsWithDates = buildRows(projectsWithDates, true);
  const rowsWithoutDates = buildRows(projectsWithoutDates, true);

  return (
    <div className="beta-milestone-view">
      {/* ─── Toolbar ─── */}
      <div className="milestone-toolbar">
        <div className="milestone-nav">
          <button className="nav-btn" onClick={() => setViewOffset((v) => v - 1)} title="上個月">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 2L4 7l5 5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            className={`nav-btn today-btn ${viewOffset === 0 ? 'active' : ''}`}
            onClick={() => setViewOffset(0)}
          >
            今天
          </button>
          <button className="nav-btn" onClick={() => setViewOffset((v) => v + 1)} title="下個月">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 2l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="nav-range-label">{rangeLabel}</span>
        </div>
      </div>

      {/* ─── Gantt Chart ─── */}
      <div className={`milestone-gantt ${selectedDetail ? 'has-detail' : ''}`}>
        {/* Fixed header row */}
        <div className="gantt-header-row">
          <div className="label-header">專案</div>
          <div className="grid-header-wrapper" ref={headerScrollRef}>
            <div className="grid-header" style={{ width: dates.length * dayWidth }}>
              {dates.map((d, i) => (
                <div
                  key={i}
                  className={`header-cell ${isWeekend(d) ? 'weekend' : ''} ${isToday(d) ? 'today' : ''}`}
                  style={{ width: dayWidth }}
                >
                  <span className="day-label">{formatDate(d)}</span>
                  <span className="weekday">
                    {['日', '一', '二', '三', '四', '五', '六'][d.getDay()]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="gantt-body">
          {/* Left labels */}
          <div className="gantt-labels" ref={labelsRef} onScroll={handleLabelsScroll}>
            {rowsWithDates.map((row, idx) => {
              if (row.type === 'project') {
                const p = row.stat.project;
                const msRange =
                  p.milestone_start_date && p.milestone_end_date
                    ? `${formatDateStr(p.milestone_start_date)} - ${formatDateStr(p.milestone_end_date)}`
                    : p.milestone_start_date
                    ? `${formatDateStr(p.milestone_start_date)} ~`
                    : `~ ${formatDateStr(p.milestone_end_date)}`;
                return (
                  <div
                    key={`p-${p.id}`}
                    className={`label-row ${selectedId === `project-${p.id}` ? 'selected' : ''}`}
                    onClick={() => handleProjectClick(p.id)}
                  >
                    <div className="label-main">
                      {row.hasPhases ? (
                        <button
                          className="expand-btn"
                          onClick={(e) => toggleProjectExpand(p.id, e)}
                        >
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 10 10"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className={row.isExpanded ? 'expanded' : ''}
                          >
                            <path d="M3 1l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      ) : (
                        <span className="expand-spacer" />
                      )}
                      <span
                        className="project-dot"
                        style={{ backgroundColor: p.color || '#667eea' }}
                      />
                      <span className="label-name">{p.name}</span>
                      <span className="label-percent">{row.stat.percent}%</span>
                    </div>
                    <div className="label-sub">
                      <span className="label-date">{msRange}</span>
                    </div>
                  </div>
                );
              }
              // Phase sub-row
              const phase = row.phase;
              const phasePercent = getPhaseProgress(phase);
              const deliverables = phase.deliverables || [];
              const phaseAllPassed = deliverables.length > 0 && deliverables.every((d) => d.qa_status === 'success');
              const phaseHasFail = deliverables.some((d) => d.qa_status === 'fail');
              const phaseQaCls = phaseHasFail ? 'qa-fail' : phaseAllPassed ? 'qa-passed' : '';
              const phaseDateRange = phase.start_date && phase.end_date
                ? `${formatDateStr(phase.start_date)} - ${formatDateStr(phase.end_date)}`
                : phase.start_date
                ? `${formatDateStr(phase.start_date)} ~`
                : phase.end_date
                ? `~ ${formatDateStr(phase.end_date)}`
                : '';
              return (
                <div
                  key={`ph-${phase.id}`}
                  className={`label-row phase-row ${phaseQaCls} ${selectedId === `phase-${phase.id}` ? 'selected' : ''}`}
                  onClick={(e) => handlePhaseClick(phase.id, e)}
                >
                  <div className="label-main">
                    <span className="phase-indent" />
                    <span className="label-name phase-name">{phase.name}</span>
                    {deliverables.length > 0 && (
                      <span className="label-percent">{phasePercent}%</span>
                    )}
                  </div>
                  {phaseDateRange && (
                    <div className="label-sub">
                      <span className="label-date">{phaseDateRange}</span>
                    </div>
                  )}
                </div>
              );
            })}
            {projectsWithoutDates.length > 0 && (
              <>
                <div className="label-divider">未設定 Milestone</div>
                {rowsWithoutDates.map((row) => {
                  if (row.type === 'project') {
                    const p = row.stat.project;
                    return (
                      <div
                        key={`p-${p.id}`}
                        className={`label-row no-date ${selectedId === `project-${p.id}` ? 'selected' : ''}`}
                        onClick={() => handleProjectClick(p.id)}
                      >
                        <div className="label-main">
                          {row.hasPhases ? (
                            <button
                              className="expand-btn"
                              onClick={(e) => toggleProjectExpand(p.id, e)}
                            >
                              <svg
                                width="10"
                                height="10"
                                viewBox="0 0 10 10"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                className={row.isExpanded ? 'expanded' : ''}
                              >
                                <path d="M3 1l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          ) : (
                            <span className="expand-spacer" />
                          )}
                          <span
                            className="project-dot"
                            style={{ backgroundColor: p.color || '#667eea' }}
                          />
                          <span className="label-name">{p.name}</span>
                          <span className="label-percent">{row.stat.percent}%</span>
                        </div>
                      </div>
                    );
                  }
                  const phase = row.phase;
                  const phasePercent = getPhaseProgress(phase);
                  const deliverables = phase.deliverables || [];
                  const ndAllPassed = deliverables.length > 0 && deliverables.every((d) => d.qa_status === 'success');
                  const ndHasFail = deliverables.some((d) => d.qa_status === 'fail');
                  const ndQaCls = ndHasFail ? 'qa-fail' : ndAllPassed ? 'qa-passed' : '';
                  return (
                    <div
                      key={`ph-${phase.id}`}
                      className={`label-row phase-row no-date ${ndQaCls} ${selectedId === `phase-${phase.id}` ? 'selected' : ''}`}
                      onClick={(e) => handlePhaseClick(phase.id, e)}
                    >
                      <div className="label-main">
                        <span className="phase-indent" />
                        <span className="label-name phase-name">{phase.name}</span>
                        {deliverables.length > 0 && (
                          <span className="label-percent">{phasePercent}%</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Right grid */}
          <div className="gantt-grid" ref={timelineRef} onScroll={handleGridScroll}>
            {/* Rows with dates */}
            {rowsWithDates.map((row) => {
              if (row.type === 'project') {
                const p = row.stat.project;
                const pos = getProjectBarPosition(p);
                const color = p.color || '#667eea';
                return (
                  <div
                    key={`p-${p.id}`}
                    className={`grid-row ${selectedId === `project-${p.id}` ? 'selected' : ''}`}
                    style={{ width: dates.length * dayWidth }}
                    onClick={() => handleProjectClick(p.id)}
                  >
                    {renderGridCells()}
                    {pos && (
                      <div
                        className="project-bar"
                        style={{ left: pos.left + 2, width: pos.width, backgroundColor: color }}
                        title={p.name}
                      >
                        <div
                          className="bar-progress"
                          style={{ width: `${row.stat.percent}%`, backgroundColor: 'rgba(255,255,255,0.25)' }}
                        />
                        <span className="bar-label">{p.name}</span>
                      </div>
                    )}
                  </div>
                );
              }
              // Phase row
              const phase = row.phase;
              const pos = getPhaseBarPosition(phase);
              const phasePercent = getPhaseProgress(phase);
              return (
                <div
                  key={`ph-${phase.id}`}
                  className={`grid-row phase-grid-row ${selectedId === `phase-${phase.id}` ? 'selected' : ''}`}
                  style={{ width: dates.length * dayWidth }}
                  onClick={(e) => handlePhaseClick(phase.id, e)}
                >
                  {renderGridCells()}
                  {pos && (
                    <div
                      className="phase-bar"
                      style={{
                        left: pos.left + 2,
                        width: pos.width,
                        backgroundColor: `${row.projectColor}60`,
                      }}
                      title={phase.name}
                    >
                      <div
                        className="bar-progress"
                        style={{
                          width: `${phasePercent}%`,
                          backgroundColor: row.projectColor,
                          opacity: 0.5,
                        }}
                      />
                      <span className="bar-label" style={{ color: '#333' }}>{phase.name}</span>
                    </div>
                  )}
                </div>
              );
            })}

            {/* No-date divider row */}
            {projectsWithoutDates.length > 0 && (
              <div
                className="grid-row divider-row"
                style={{ width: dates.length * dayWidth }}
              >
                {renderGridCells()}
              </div>
            )}

            {/* No-date rows */}
            {rowsWithoutDates.map((row) => {
              if (row.type === 'project') {
                return (
                  <div
                    key={`p-${row.stat.project.id}`}
                    className={`grid-row no-date-row ${selectedId === `project-${row.stat.project.id}` ? 'selected' : ''}`}
                    style={{ width: dates.length * dayWidth }}
                    onClick={() => handleProjectClick(row.stat.project.id)}
                  >
                    {renderGridCells()}
                  </div>
                );
              }
              return (
                <div
                  key={`ph-${row.phase.id}`}
                  className={`grid-row phase-grid-row no-date-row ${selectedId === `phase-${row.phase.id}` ? 'selected' : ''}`}
                  style={{ width: dates.length * dayWidth }}
                  onClick={(e) => handlePhaseClick(row.phase.id, e)}
                >
                  {renderGridCells()}
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── Detail Drawer (right side) ─── */}
        {selectedDetail?.type === 'project' && selectedDetail.data && (() => {
          const ss = selectedDetail.data;
          return (
            <div className="milestone-detail" key={`project-${ss.project.id}`}>
              <button className="detail-close-btn" onClick={() => setSelectedId(null)} title="關閉">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
                </svg>
              </button>
              <div className="detail-card">
                <div className="detail-header">
                  <div className="detail-project-info">
                    <span className="project-color" style={{ backgroundColor: ss.project.color || '#667eea' }} />
                    <h4 className="project-name" onClick={() => onNavigate?.('project', ss.project.id)}>
                      {ss.project.name}
                    </h4>
                  </div>
                  {(ss.project.milestone_start_date || ss.project.milestone_end_date) && (
                    <span className="detail-date-range">
                      {formatDateStr(ss.project.milestone_start_date)} ~ {formatDateStr(ss.project.milestone_end_date)}
                    </span>
                  )}
                </div>

                <div className="detail-progress">
                  <div className="progress-bar-track">
                    <div className="progress-bar-fill" style={{ width: `${ss.percent}%`, backgroundColor: ss.project.color || '#667eea' }} />
                  </div>
                  <div className="progress-info">
                    <span className="progress-percent">{ss.percent}%</span>
                    <span className="progress-detail">{ss.completed}/{ss.total} Tickets 完成</span>
                  </div>
                </div>

                <div className="detail-breakdown">
                  <div className="breakdown-item">
                    <span className="dot" style={{ backgroundColor: '#f59e0b' }} />
                    <span className="breakdown-label">待處理</span>
                    <span className="breakdown-count">{ss.pending}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="dot" style={{ backgroundColor: '#3b82f6' }} />
                    <span className="breakdown-label">進行中</span>
                    <span className="breakdown-count">{ss.inProgress}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="dot" style={{ backgroundColor: '#10b981' }} />
                    <span className="breakdown-label">已完成</span>
                    <span className="breakdown-count">{ss.completed}</span>
                  </div>
                </div>

                {ss.tickets.length > 0 && (
                  <div className="detail-tickets">
                    <div className="detail-section-label">Tickets</div>
                    {(expandedTickets ? ss.tickets : ss.tickets.slice(0, 5)).map((ticket) => {
                      const firstAssignee = (ticket.ticket_assignees || [])[0];
                      const ownerEmail = firstAssignee?.profiles?.email?.toLowerCase();
                      const ownerName = ownerEmail ? usersMapByEmail[ownerEmail]?.name : null;
                      return (
                        <div key={ticket.id} className="detail-ticket-row" onClick={() => onNavigate?.('ticket', ss.project.id, ticket.id, { ticketNo: ticket.ticket_no })}>
                          <span className="ticket-status-dot" style={{
                            backgroundColor: ticket.status === 'completed' ? '#10b981' : ticket.status === 'in_progress' ? '#3b82f6' : '#f59e0b',
                          }} />
                          <span className="ticket-id">#{ticket.ticket_no}</span>
                          <span className="ticket-title">{ticket.title}</span>
                          {ownerName && <span className="ticket-owner">{ownerName}</span>}
                          {ticket.end_date && <span className="ticket-date">{formatDateStr(ticket.end_date)}</span>}
                        </div>
                      );
                    })}
                    {ss.tickets.length > 5 && !expandedTickets && (
                      <div className="show-more" onClick={() => setExpandedTickets(true)}>
                        展開全部 {ss.tickets.length} 個 Ticket
                      </div>
                    )}
                    {expandedTickets && ss.tickets.length > 5 && (
                      <div className="show-more" onClick={() => setExpandedTickets(false)}>收合</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {selectedDetail?.type === 'phase' && selectedDetail.data && (() => {
          const phase = selectedDetail.data;
          const deliverables = phase.deliverables || [];
          const allItems = deliverables.flatMap((d) => d.deliverable_items || []);
          const totalItems = allItems.length;
          const doneItems = allItems.filter((i) => i.is_completed).length;
          const phasePercent = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;
          const projectColor = selectedDetail.projectStat?.project?.color || '#667eea';
          const allPassed = deliverables.length > 0 && deliverables.every((d) => d.qa_status === 'success');
          const hasFail = deliverables.some((d) => d.qa_status === 'fail');
          const phaseQaClass = hasFail ? 'phase-qa-fail' : allPassed ? 'phase-qa-passed' : '';
          return (
            <div className="milestone-detail" key={`phase-${phase.id}`}>
              <button className="detail-close-btn" onClick={() => setSelectedId(null)} title="關閉">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
                </svg>
              </button>
              <div className={`detail-card ${phaseQaClass}`}>
                <div className="detail-header">
                  <div className="detail-project-info">
                    <span className="project-color" style={{ backgroundColor: projectColor }} />
                    <h4
                      className="project-name clickable"
                      title="前往專案 Phases 頁面"
                      onClick={() => onNavigate?.('project-phases', selectedDetail.projectStat?.project?.id, phase.id)}
                    >
                      {phase.name}
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginLeft: 4, opacity: 0.5 }}>
                        <path d="M4.5 2.5h5v5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.5 2.5L2.5 9.5" strokeLinecap="round" />
                      </svg>
                    </h4>
                  </div>
                  {(phase.start_date || phase.end_date) && (
                    <span className="detail-date-range">
                      {formatDateStr(phase.start_date)} ~ {formatDateStr(phase.end_date)}
                    </span>
                  )}
                </div>

                <div className="detail-progress">
                  <div className="progress-bar-track">
                    <div className="progress-bar-fill" style={{ width: `${phasePercent}%`, backgroundColor: projectColor }} />
                  </div>
                  <div className="progress-info">
                    <span className="progress-percent">{phasePercent}%</span>
                    <span className="progress-detail">{doneItems}/{totalItems} 工作項目完成</span>
                  </div>
                </div>

                {deliverables.length > 0 && (
                  <div className="detail-tickets">
                    <div className="detail-section-label">交付項目</div>
                    {deliverables.map((d) => {
                      const items = d.deliverable_items || [];
                      const dCount = items.filter((i) => i.is_completed).length;
                      const remainingHours = items.filter((i) => !i.is_completed).reduce((sum, i) => sum + (parseFloat(i.estimated_hours) || 0), 0);
                      const actualHoursTotal = items.reduce((sum, i) => sum + (parseFloat(i.actual_hours) || 0), 0);
                      const roles = [...new Set(items.map((i) => i.role_type))];
                      return (
                        <div key={d.id} className="detail-ticket-row" style={{ cursor: 'default' }}>
                          {d.qa_status === 'success' && (
                            <span className="qa-badge qa-pass">Pass</span>
                          )}
                          {d.qa_status === 'fail' && (
                            <span className="qa-badge qa-fail">Fail</span>
                          )}
                          {!d.qa_status && (
                            <span className="qa-badge qa-pending">未驗證</span>
                          )}
                          <span className="ticket-title">{d.title}</span>
                          {items.length > 0 && (
                            <span className="ticket-owner">{dCount}/{items.length}</span>
                          )}
                          {roles.length > 0 && (
                            <span className="ticket-date" style={{ fontSize: 10 }}>{roles.join(', ')}</span>
                          )}
                          {remainingHours > 0 && (
                            <span className="ticket-hours">剩餘 {remainingHours}h</span>
                          )}
                          {actualHoursTotal > 0 && (
                            <span className="ticket-hours" style={{ color: '#f59e0b' }}>實際 {actualHoursTotal}h</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default BetaMilestoneView;
